import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  setCurrentPage,
  setSelectedBranch,
} from "../../redux/admin/branch.slice";

import {
  fetchBranches,
  addBranch,
  updateBranch,
} from "../../redux/admin/branch.thunk";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

const Branches = () => {
  const dispatch = useDispatch();
  const limit = 10;

  const {
    branches = [],
    totalPages = 1,
    currentPage = 1,
    total = 0,
    loading = false,
    selectedBranch = null,
  } = useSelector((state) => state.admin?.branches || {});

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("details");

  const initialFormState = {
    branch_name: "",
    branch_code: "",
    contact_person: "",
    phone_number: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    status: "active",
  };

  const [formData, setFormData] = useState(initialFormState);

  /* ==========================================
  SEARCH DEBOUNCE
  ========================================== */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 600);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  /* ==========================================
  FETCH DATA
  ========================================== */
  useEffect(() => {
    dispatch(
      fetchBranches({
        page: currentPage,
        limit,
        search: debouncedSearch,
      })
    );
  }, [dispatch, currentPage, debouncedSearch]);

  /* ==========================================
  PAGINATION
  ========================================== */
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      dispatch(setCurrentPage(newPage));
    }
  };

  /* ==========================================
  FORM CHANGE
  ========================================== */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? "active" : "inactive") : value,
    }));
  };

  /* ==========================================
  ADD BRANCH
  ========================================== */
  const openAddModal = () => {
    setIsEditMode(false);
    setFormData(initialFormState);
    setActiveTab("details");
    setShowModal(true);
  };

  /* ==========================================
  EDIT BRANCH
  ========================================== */
  const openEditModal = (branch) => {
    setIsEditMode(true);
    dispatch(setSelectedBranch(branch));
    setFormData({
      ...initialFormState,
      ...branch,
      status: branch.status || "active",
    });
    setActiveTab("details");
    setShowModal(true);
  };

  /* ==========================================
  SUBMIT
  ========================================== */
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!formData.branch_name || !formData.branch_code) {
      toastr.warning("Branch Name and Branch Code are required");
      return;
    }

    try {
      if (isEditMode) {
        await dispatch(
          updateBranch({
            id: selectedBranch.id,
            branchData: formData,
          })
        ).unwrap();
        toastr.success("Branch Updated Successfully");
      } else {
        await dispatch(addBranch(formData)).unwrap();
        toastr.success("Branch Created Successfully");
      }

      setShowModal(false);
      dispatch(
        fetchBranches({
          page: currentPage,
          limit,
          search: debouncedSearch,
        })
      );
    } catch (err) {
      toastr.error(err || "Process Failed");
    }
  };

  /* ==========================================
  PAGINATION NUMBERS
  ========================================== */
  const renderPagination = () => {
    let pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          type="button"
          onClick={() => handlePageChange(i)}
          className={`mnc-page-num ${currentPage === i ? "active" : ""}`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <div className="mnc-page-bg">
      <div className="container-fluid px-4 py-4">
        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div>
            <h3 className="fw-800 text-dark mb-1">Branch Registry</h3>
            <div className="d-flex align-items-center gap-2">
              <span className="badge-total shadow-sm">
                Total Branches: {total}
              </span>
              <span className="text-muted small fw-600">| Branch Master</span>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-mnc-primary shadow-sm"
            onClick={openAddModal}
          >
            <i className="fas fa-plus-circle me-2"></i>
            New Branch
          </button>
        </div>

        {/* SEARCH */}
        <div className="search-bar-mnc shadow-sm mb-4">
          <i className="fas fa-search me-3 text-muted"></i>
          <input
            type="text"
            placeholder="Search by branch name, code or phone number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* TABLE */}
        <div className="mnc-card shadow-sm">
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead className="mnc-thead">
                <tr>
                  <th className="ps-4">BRANCH DETAILS</th>
                  <th>BRANCH CODE</th>
                  <th>CITY</th>
                  <th>CONTACT</th>
                  <th>STATUS</th>
                  <th className="text-end pe-4">MANAGEMENT</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div className="spinner-border text-primary spinner-border-sm"></div>
                    </td>
                  </tr>
                ) : branches.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5 text-muted">
                      No Branches Found
                    </td>
                  </tr>
                ) : (
                  branches.map((branch) => (
                    <tr key={branch.id} className="mnc-row">
                      <td className="ps-4">
                        <div className="d-flex align-items-center">
                          <div
                            className={`mnc-avatar-mini ${
                              branch.status === "active" ? "active" : "inactive"
                            }`}
                          >
                            {branch.branch_name?.[0]}
                          </div>
                          <div className="ms-3">
                            <div className="fw-700 text-dark small">
                              {branch.branch_name}
                            </div>
                            <div className="text-muted x-small">
                              {branch.address}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="mnc-id-tag">{branch.branch_code}</span>
                      </td>
                      <td>{branch.city || "N/A"}</td>
                      <td>{branch.phone_number || "N/A"}</td>
                      <td>
                        <span
                          className={`mnc-status-pill ${
                            branch.status === "active" ? "active" : "inactive"
                          }`}
                        >
                          {branch.status?.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-end pe-4">
                        <button
                          type="button"
                          className="btn-mnc-outline"
                          onClick={() => openEditModal(branch)}
                        >
                          Edit Branch
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          <div className="mnc-footer-pagination">
            <div className="text-muted x-small fw-700 uppercase">
              Showing {branches.length} of {total}
            </div>

            <div className="mnc-pagination-hub">
              <button
                type="button"
                className="mnc-page-arrow"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                <i className="fas fa-chevron-left"></i>
              </button>

              {renderPagination()}

              <button
                type="button"
                className="mnc-page-arrow"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="mnc-modal-overlay">
          <div className="mnc-modal-window">
            {/* SIDEBAR */}
            <div className="mnc-modal-side">
              <div className="p-4 border-bottom">
                <h6 className="fw-800 text-dark mb-0">Branch Master</h6>
                <p className="x-small text-muted mb-0">Branch Management</p>
              </div>

              <div className="flex-grow-1 p-2">
                <button
                  type="button"
                  className={`mnc-tab-link ${
                    activeTab === "details" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("details")}
                >
                  <i className="fas fa-building me-2"></i>
                  Branch Details
                </button>

                <button
                  type="button"
                  className={`mnc-tab-link ${
                    activeTab === "address" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("address")}
                >
                  <i className="fas fa-map-marker-alt"></i>
                  Address Details
                </button>
              </div>

              <div className="p-3 mt-auto">
                <div
                  className={`mnc-status-linear ${
                    formData.status === "active" ? "is-active" : "is-inactive"
                  }`}
                >
                  <div className="d-flex align-items-center flex-grow-1">
                    <div className="mnc-status-indicator"></div>
                    <span className="mnc-status-text">
                      {formData.status?.toUpperCase()}
                    </span>
                  </div>

                  <div className="form-check form-switch m-0 p-0">
                    <input
                      className="form-check-input mnc-switch"
                      type="checkbox"
                      name="status"
                      checked={formData.status === "active"}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* MAIN CONTENT AREA */}
            <form onSubmit={handleSubmit} className="mnc-modal-main">
              <div className="mnc-modal-header">
                <div className="d-flex justify-content-between align-items-center w-100">
                  <div>
                    <h5 className="fw-800 mb-0">
                      {isEditMode ? "Update Branch" : "Add New Branch"}
                    </h5>
                    <span className="text-muted x-small fw-600">
                      CODE : {formData.branch_code || "PENDING"}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn-close shadow-none"
                    onClick={() => setShowModal(false)}
                  ></button>
                </div>
              </div>

              <div className="mnc-modal-scroller">
                <div className="row g-3">
                  {activeTab === "details" && (
                    <>
                      <div className="col-md-6">
                        <label className="mnc-label">Branch Name *</label>
                        <input
                          type="text"
                          name="branch_name"
                          className="mnc-field"
                          value={formData.branch_name}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="mnc-label">Branch Code *</label>
                        <input
                          type="text"
                          name="branch_code"
                          disabled={isEditMode}
                          className="mnc-field"
                          value={formData.branch_code}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="mnc-label">Contact Person</label>
                        <input
                          type="text"
                          name="contact_person"
                          className="mnc-field"
                          value={formData.contact_person}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="mnc-label">Phone Number</label>
                        <input
                          type="text"
                          name="phone_number"
                          className="mnc-field"
                          value={formData.phone_number}
                          onChange={handleChange}
                        />
                      </div>
                    </>
                  )}

                  {activeTab === "address" && (
                    <>
                      <div className="col-md-12">
                        <label className="mnc-label">Address</label>
                        <textarea
                          rows="3"
                          name="address"
                          className="mnc-field"
                          value={formData.address}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="col-md-4">
                        <label className="mnc-label">City</label>
                        <input
                          type="text"
                          name="city"
                          className="mnc-field"
                          value={formData.city}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="col-md-4">
                        <label className="mnc-label">State</label>
                        <input
                          type="text"
                          name="state"
                          className="mnc-field"
                          value={formData.state}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="col-md-4">
                        <label className="mnc-label">Pincode</label>
                        <input
                          type="text"
                          name="pincode"
                          className="mnc-field"
                          value={formData.pincode}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="col-md-12">
                        <label className="mnc-label">Email</label>
                        <input
                          type="email"
                          name="email"
                          className="mnc-field"
                          value={formData.email}
                          onChange={handleChange}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="mnc-modal-footer">
                <button
                  type="button"
                  className="btn-mnc-discard"
                  onClick={() => setShowModal(false)}
                >
                  Discard
                </button>
                <button type="submit" className="btn-mnc-sync">
                  {isEditMode ? "Update Branch" : "Create Branch"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .mnc-page-bg {
          background: #f4f7fa;
          min-height: 100vh;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }

        .fw-800 { font-weight: 800; }
        .fw-700 { font-weight: 700; }
        .fw-600 { font-weight: 600; }
        .x-small { font-size: 0.72rem; }
        
        .uppercase {
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .btn-mnc-primary {
          background: #0f172a;
          color: white;
          border-radius: 10px;
          padding: 10px 22px;
          font-weight: 700;
          border: none;
          font-size: 0.85rem;
          transition: background 0.2s;
        }
        .btn-mnc-primary:hover { background: #1e293b; }

        .btn-mnc-sync {
          background: #6366f1;
          color: white;
          border-radius: 8px;
          padding: 10px 24px;
          font-weight: 700;
          border: none;
          font-size: 0.85rem;
          transition: background 0.2s;
        }
        .btn-mnc-sync:hover { background: #4f46e5; }

        .btn-mnc-discard {
          background: #fff;
          color: #64748b;
          border-radius: 8px;
          padding: 10px 24px;
          font-weight: 700;
          border: 1px solid #e2e8f0;
          font-size: 0.85rem;
          transition: background 0.2s;
        }
        .btn-mnc-discard:hover { background: #f8fafc; }

        .btn-mnc-outline {
          background: transparent;
          border: 1px solid #e2e8f0;
          padding: 6px 15px;
          border-radius: 8px;
          font-weight: 700;
          font-size: 0.75rem;
          color: #6366f1;
          transition: all 0.2s;
        }
        .btn-mnc-outline:hover {
          background: #6366f1;
          color: white;
          border-color: #6366f1;
        }

        .badge-total {
          background: #6366f1;
          color: white;
          padding: 5px 15px;
          border-radius: 100px;
          font-weight: 800;
          font-size: 0.75rem;
        }

        .mnc-card {
          background: white;
          border-radius: 20px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }

        .mnc-thead th {
          background: #f8fafc;
          color: #64748b;
          font-size: 0.65rem;
          font-weight: 800;
          padding: 16px 15px;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          border-bottom: 1px solid #e2e8f0;
        }

        .mnc-row {
          border-bottom: 1px solid #f1f5f9;
        }
        .mnc-row:last-child { border-bottom: none; }

        .mnc-avatar-mini {
          width: 38px;
          height: 38px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          color: white;
          text-transform: uppercase;
        }
        .mnc-avatar-mini.active { background: #10b981; }
        .mnc-avatar-mini.inactive { background: #f43f5e; }

        .mnc-status-pill {
          padding: 4px 12px;
          border-radius: 100px;
          font-size: 0.6rem;
          font-weight: 800;
          display: inline-block;
        }
        .mnc-status-pill.active { background: #dcfce7; color: #15803d; }
        .mnc-status-pill.inactive { background: #fee2e2; color: #b91c1c; }

        .mnc-id-tag {
          background: #f1f5f9;
          padding: 4px 8px;
          border-radius: 6px;
          font-weight: 700;
          font-size: 0.75rem;
          color: #334155;
        }

        .mnc-footer-pagination {
          padding: 15px 25px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-top: 1px solid #f1f5f9;
        }

        .mnc-pagination-hub {
          display: flex;
          gap: 5px;
          align-items: center;
        }

        .mnc-page-num, .mnc-page-arrow {
          width: 32px;
          height: 32px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
          font-weight: 600;
          color: #475569;
          transition: all 0.2s;
        }
        .mnc-page-num.active {
          background: #6366f1;
          color: white;
          border-color: #6366f1;
        }
        .mnc-page-num:not(.active):hover, .mnc-page-arrow:not(:disabled):hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
        }
        .mnc-page-arrow:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* MODAL WINDOW CUSTOM CONFIGS */
        .mnc-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.4);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          padding: 20px;
        }

        .mnc-modal-window {
          width: 100%;
          max-width: 920px;
          height: 580px;
          background: white;
          border-radius: 20px;
          display: flex;
          overflow: hidden;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }

        .mnc-modal-side {
          width: 250px;
          background: #f8fafc;
          border-right: 1px solid #e2e8f0;
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
        }

        .mnc-modal-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-width: 0; /* Clear grid layout blowout flags */
          background: #fff;
        }

        .mnc-modal-header {
          padding: 24px 30px;
          border-bottom: 1px solid #f1f5f9;
        }

        .mnc-modal-scroller {
          flex: 1;
          overflow-y: auto;
          padding: 30px;
        }

        .mnc-modal-footer {
          padding: 20px 30px;
          border-top: 1px solid #f1f5f9;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          background: #f8fafc;
        }

        .mnc-tab-link {
          border: none;
          background: none;
          text-align: left;
          padding: 12px 20px;
          width: 100%;
          border-radius: 10px;
          font-weight: 700;
          font-size: 0.8rem;
          color: #64748b;
          display: flex;
          align-items: center;
          gap: 10px;
          transition: all 0.2s;
        }
        .mnc-tab-link.active {
          background: white;
          color: #6366f1;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03);
        }

        .mnc-label {
          font-size: 0.65rem;
          font-weight: 800;
          color: #64748b;
          text-transform: uppercase;
          margin-bottom: 6px;
          display: block;
          letter-spacing: 0.05em;
        }

        .mnc-field {
          width: 100%;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 10px 14px;
          font-size: 0.85rem;
          color: #1e293b;
          background-color: #fff;
          transition: border-color 0.2s;
        }
        .mnc-field:focus {
          outline: none;
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }
        .mnc-field:disabled {
          background-color: #f1f5f9;
          color: #94a3b8;
          cursor: not-allowed;
        }

        /* Status linear switch panel block layout */
        .mnc-status-linear {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #fff;
          border: 1px solid #e2e8f0;
          padding: 12px 16px;
          border-radius: 12px;
        }
        .mnc-status-indicator {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          margin-right: 8px;
        }
        .is-active .mnc-status-indicator { background: #10b981; }
        .is-inactive .mnc-status-indicator { background: #f43f5e; }
        .mnc-status-text {
          font-size: 0.75rem;
          font-weight: 800;
          color: #334155;
        }
        .mnc-switch {
          cursor: pointer;
        }

        .search-bar-mnc {
          background: white;
          padding: 12px 20px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          max-width: 550px;
          border: 1px solid #e2e8f0;
        }
        .search-bar-mnc input {
          border: none;
          outline: none;
          width: 100%;
          font-size: 0.85rem;
        }
      `}</style>
    </div>
  );
};

export default Branches;